/** @jsxImportSource @emotion/core */
import { css } from '@emotion/core';
import React, { FC, Fragment, useMemo } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { MdCreate, MdFileDownload } from 'react-icons/md';
import { ImProfile } from 'react-icons/im';
import { NavLink, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Box, Flex } from 'components/styled';
import Text from 'components/styled/Text';
import ItemLabel, { ItemLabelList } from 'components/styled/ItemLabel';
import Checkbox from 'components/form/checkbox/Checkbox';
import AspectRatio from 'components/styled/AspectRatio';
import Divider from 'components/styled/Divider';
import IconButton from 'components/styled/IconButton';
import FloatingActionsButton from 'components/floatingactions/FAButton';
import SubButton from 'components/floatingactions/SubButton';
import OfferDownloadCVDialog from 'components/modal/OfferDownloadCVDialog';

import { getDateString, pluralRules } from 'utils';
import { PersonIcon } from 'assets';
import { useUserToken } from 'auth';

import { generateAllCVofCandidates } from 'api/studentApi';

import { isAdminUser, isRecruiterUser } from 'auth/token';

import { useExportItem } from 'hooks/useExport';
import useToggleOfferActive from 'hooks/useToggleOfferActive';

import { isOfferActive } from 'utils/UASHelpers';
import { EmploymentTypeText, SortedEmploymentTexts } from 'utils/enumMaps';
import { getAddressLabel } from 'utils/address';

import OfferTileHeader from './offers/OfferTileHeader';

import { Offer, OfferDto } from 'typing/endpoints';

type InactiveProps = { variant: 'inactive'; isVisible: false } & Pick<
	Offer | OfferDto,
	| 'id'
	| 'title'
	| 'customTitle'
	| 'company'
	| 'addresses'
	| 'employmentTypes'
	| 'businessSectors'
>;

type SearchProps = { variant: 'search' } & Pick<
	Offer | OfferDto,
	| 'id'
	| 'title'
	| 'customTitle'
	| 'company'
	| 'addresses'
	| 'employmentTypes'
	| 'businessSectors'
	| 'validTo'
	| 'validFrom'
	| 'isAcceptingForeignStudents'
	| 'suitableForSPP'
>;

type EditableProps = {
	variant: 'editable' | 'admin' | 'profile';
	canDisable?: boolean;
	disableTitle?: string;
} & Pick<
	Offer,
	| 'id'
	| 'title'
	| 'customTitle'
	| 'company'
	| 'addresses'
	| 'employmentTypes'
	| 'businessSectors'
	| 'isVisible'
	| 'submittedForms'
	| 'validTo'
	| 'validFrom'
	| 'activationCounter'
	| 'historyOfActiveDateRanges'
	| 'cvOfStudents'
	| 'suitableForSPP'
>;

type Props = {
	hideLogo?: boolean;
	ratio?: [number, number];
} & (SearchProps | EditableProps | InactiveProps);

const OfferSearchItem: FC<Props> = props => {
	const {
		id,

		company,
		addresses,
		employmentTypes,
		businessSectors,
		hideLogo,
		ratio,
		...p
	} = props;
	// History
	const { push } = useHistory();

	const isActive = useMemo(
		() =>
			p.variant === 'inactive'
				? false
				: p.variant === 'search' || isOfferActive(p),
		[p],
	);

	// Export
	const { exporting, selected, toggleSelected } = useExportItem(id);

	// Callbacks
	const token = useUserToken();
	const isAdmin = isAdminUser(token);
	const toggleOfferActive = useToggleOfferActive(
		isActive,
		id,
		isRecruiterUser(token),
	);

	const historyOfActiveDateRanges = useMemo(() => {
		if (p.variant === 'admin') {
			const actual =
				p.validFrom && p.validTo ? [{ from: p.validFrom, to: p.validTo }] : [];
			return [
				...actual,
				...p.historyOfActiveDateRanges.map(hadr => ({
					from: hadr.from,
					to: hadr.to,
				})),
			];
		} else {
			return [];
		}
	}, [p]);

	return (
		<AspectRatio ratio={ratio ?? [3, 2]} width="100%">
			<Flex
				height="100%"
				bg="white"
				boxShadow={`0px 0px 12px rgba(0, 0, 0, ${selected ? '0.5' : '0.1'})`}
			>
				<Box
					pl={1}
					backgroundColor={
						!isActive ? 'darkGrey' : company?.color ?? 'secondary'
					}
				/>
				<Flex
					flexDirection="column"
					alignItems="flex-start"
					flexGrow={1}
					px={20}
					py={20}
					position="relative"
					width={1}
				>
					{/* Export */}
					{exporting && (
						<Checkbox checked={selected} onChange={toggleSelected} />
					)}
					<OfferTileHeader
						offer={({ ...props } as unknown) as OfferDto}
						hideLogo={hideLogo}
						tileVariant={p.variant}
					/>
					<Box flexGrow={1} />
					{p.variant !== 'editable' && (
						<React.Fragment>
							{employmentTypes && employmentTypes.length > 0 && (
								<ItemLabelList
									wrapperCss={css`
										max-width: 100%;
									`}
								>
									{employmentTypes
										.sort(
											(a, b) =>
												SortedEmploymentTexts[a] - SortedEmploymentTexts[b],
										)
										.map(t => EmploymentTypeText[t])}
								</ItemLabelList>
							)}
							<Flex width="100%">
								{businessSectors && businessSectors.length > 0 && (
									<ItemLabelList
										wrapperCss={css`
											max-width: 100%;
										`}
									>
										{businessSectors.map(b => b?.name)}
									</ItemLabelList>
								)}
							</Flex>
							{addresses && addresses.length > 0 && (
								<ItemLabelList
									wrapperCss={css`
										max-width: 100%;
									`}
								>
									{addresses.map(address => getAddressLabel(address))}
								</ItemLabelList>
							)}
						</React.Fragment>
					)}
					{p.variant !== 'search' && p.variant !== 'inactive' && (
						<Flex
							alignItems="flex-start"
							flexDirection={p.variant === 'admin' ? 'row' : 'column'}
							width="100%"
						>
							<ItemLabel as="button" backgroundColor="white" lineHeight="21px">
								<NavLink
									to={`/offer/candidates/${id}`}
									target="_blank"
									css={css`
										text-decoration: none;
									`}
								>
									<Text color="text" m={0} p={0}>
										<PersonIcon height={10} mr={1} />
										{pluralRules(p.submittedForms.length, {
											one: 'zájemce',
											few: 'zájemci',
											many: 'zájemců',
										})}
									</Text>
								</NavLink>
							</ItemLabel>
						</Flex>
					)}
					<Flex width={1}>
						{p.variant === 'admin' &&
							(historyOfActiveDateRanges?.length ?? 0) > 0 && (
								<ItemLabelList
									wrapperCss={css`
										max-width: initial !important;
									`}
								>
									{historyOfActiveDateRanges.map((hadr, index) => (
										<Text m={0} key={index} fontSize="sm">
											Aktivní: od: <b>{getDateString(hadr.from)}</b> do:{' '}
											<b>{getDateString(hadr.to)}</b>
										</Text>
									))}
								</ItemLabelList>
							)}
						{p.variant === 'search' && p.validTo && (
							<ItemLabel
								css={css`
									max-width: initial;
								`}
							>
								<Text m={0} fontSize="sm">
									Aktivní do: <b>{getDateString(p.validTo)}</b>
								</Text>
							</ItemLabel>
						)}
					</Flex>
					{p.variant !== 'inactive' && (
						<Flex
							width="100%"
							flexDirection={isAdmin ? 'row' : 'column'}
							alignItems="flex-start"
						>
							{!isActive && (
								<ItemLabel
									color="inverted"
									css={css`
										max-width: initial;
									`}
								>
									{!p.validTo ? 'Nezveřejněná' : 'Neaktivní'}
								</ItemLabel>
							)}

							{p.variant === 'search' && p?.isAcceptingForeignStudents && (
								<ItemLabel
									title="Foreign employees welcome"
									css={css`
										max-width: initial;
									`}
								>
									<Text as="span" color="primary">
										+{' '}
									</Text>
									Foreign employees welcome
								</ItemLabel>
							)}

							{p.variant === 'search' && p?.suitableForSPP && (
								<ItemLabel
									title="Vhodné pro studenty se specifickými potřebami"
									css={css`
										max-width: initial;
									`}
								>
									<Text as="span" color="primary">
										+{' '}
									</Text>
									Vhodné pro SSP
								</ItemLabel>
							)}

							{p.variant !== 'search' && (
								<Fragment>
									<Box flexGrow={1} />
									{
										// TODO:FIXME: remove constant true condition when an agreement is reached on the method and UI with the customer.
										isAdmin ? (
											<>
												<FloatingActionsButton id={`${id}-card-actions`}>
													{p.cvOfStudents && (
														<SubButton
															icon={<MdFileDownload size={15} color="white" />}
															size={35}
															tooltip="Stáhnout CV všech zájemců"
															onClick={() =>
																window.open(`/api/files/${p.cvOfStudents.id}`)
															}
															backgroundColor="secondary"
														/>
													)}
													{p.submittedForms.length > 0 && (
														<SubButton
															icon={<ImProfile size={15} color="white" />}
															size={35}
															tooltip="Vytvořit požadavek na stáhnutí CV všech zájemců"
															onClick={async () => {
																try {
																	const resp = await generateAllCVofCandidates(
																		id,
																	);
																	if (resp.ok) {
																		toast.success(
																			'Požadavek na stáhnutí CV zájemců vytvořen. Po dokončení budete upozorněni notifikací.',
																		);
																	} else {
																		toast.error(
																			'Požadavek na stáhnutí CV zájemců se nepodařilo vytvořit. Zkuste to prosím později.',
																		);
																	}
																} catch (error) {
																	toast.error(
																		'Požadavek na stáhnutí CV zájemců se nepodařilo vytvořit. Zkuste to prosím později.',
																	);
																}
															}}
															backgroundColor="secondary"
														/>
													)}
													<SubButton
														icon={<MdCreate size={15} color="white" />}
														size={35}
														tooltip="Upravit"
														onClick={() => push(`/offer/edit/${id}`)}
														backgroundColor="primary"
													/>
													<SubButton
														icon={
															isActive ? (
																<FaEyeSlash size={15} color="white" />
															) : (
																<FaEye size={15} color="white" />
															)
														}
														size={35}
														tooltip={isActive ? 'Deaktivovat' : 'Aktivovat'}
														onClick={toggleOfferActive}
														backgroundColor={isActive ? 'error' : 'primary'}
														disabled={isActive ? false : p.canDisable}
														title={p.disableTitle}
													/>
												</FloatingActionsButton>
											</>
										) : (
											<Flex width={1} flexDirection="column">
												<Divider
													my={1}
													borderColor="rgba(0,0,0,0.1)"
													size={1}
												/>

												<Flex
													width={1}
													justifyContent="space-between"
													alignItems="center"
												>
													{p.submittedForms.length > 0 && (
														<OfferDownloadCVDialog
															cvOfStudents={p.cvOfStudents}
															offerId={id}
															control={openModal => (
																<IconButton
																	width={1 / 4}
																	css={css`
																		white-space: nowrap;
																		text-overflow: ellipsis;
																		overflow: hidden;
																	`}
																	title="Stáhnout CV všech zájemců"
																	onClick={openModal}
																	bg="secondary"
																	p={2}
																>
																	<Flex
																		flexDirection="column"
																		alignItems="center"
																	>
																		<MdFileDownload size={15} color="black" />
																		Stáhnout CV
																	</Flex>
																</IconButton>
															)}
														/>
													)}

													<IconButton
														width={1 / 4}
														title="Upravit"
														onClick={() => push(`/offer/edit/${id}`)}
														bg="secondary"
														p={2}
													>
														<Flex flexDirection="column" alignItems="center">
															<MdCreate size={15} color="black" /> Upravit
														</Flex>
													</IconButton>
													<IconButton
														width={1 / 4}
														title={isActive ? 'Deaktivovat' : 'Aktivovat'}
														onClick={toggleOfferActive}
														bg="secondary"
														p={2}
													>
														<Flex flexDirection="column" alignItems="center">
															{isActive ? (
																<FaEyeSlash size={16} />
															) : (
																<FaEye size={16} />
															)}
															{isActive ? ' Deaktivovat' : ' Aktivovat'}
														</Flex>
													</IconButton>
												</Flex>
											</Flex>
										)
									}
								</Fragment>
							)}
						</Flex>
					)}
				</Flex>
			</Flex>
		</AspectRatio>
	);
};
export default OfferSearchItem;
